import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeeAdditionalCompensationValue } from "@/pages/employees/index/employee-column-values.ts";
import {
  ApiEmployeeColumn,
  ApiEmployeeColumnValueType,
  EmployeeColumnEntity,
  getEmployeeColumnEntity,
  parseCustomFieldKey,
  parseSystemTableCustomFieldKey,
  parseCustomTableKey,
} from "@/types/employees/employee-column.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { Stack, Text } from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";

const EMPTY = "-";
const TEAM_COLUMN_KEY = "employee.team";
const TEAM_ROLE_COLUMN_KEY = "employee.team_role";

const sizeByValueType = (column: ApiEmployeeColumn) => {
  switch (column.value_type) {
    case ApiEmployeeColumnValueType.employee:
      return ColumnSizes.employee;
    case ApiEmployeeColumnValueType.date:
    case ApiEmployeeColumnValueType.datetime:
      return ColumnSizes.date;
    case ApiEmployeeColumnValueType.bool:
      return ColumnSizes.status;
    default:
      return ColumnSizes.standard;
  }
};

// У разовых выплат и кастомных таблиц в ячейке может быть несколько значений.
function renderRows(rows: ReactNode[]) {
  const filled = rows.filter((row) => row !== null && row !== undefined && row !== EMPTY);
  if (!filled.length) return EMPTY;
  if (filled.length === 1) return filled[0];
  return (
    <Stack direction="vertical">
      {filled.map((row, index) => (
        <Text key={index} className="truncate">
          {row}
        </Text>
      ))}
    </Stack>
  );
}

/** Рендеры полей таблиц и доп. компенсаций, которых нет в legacy-определениях. */
export const useEmployeeCatalogColumns = () => {
  const { t } = useTranslation();
  const { renderDate, renderDateAndTime, renderCurrency } = useFormatter();
  const { renderFieldDataInCell } = useCustomFieldsHook();

  const renderScalar = useCallback(
    (value: unknown, column: ApiEmployeeColumn): ReactNode => {
      if (value === null || value === undefined || value === "") return EMPTY;

      switch (column.value_type) {
        case ApiEmployeeColumnValueType.date:
          return renderDate(value instanceof Date ? value : new Date(String(value)));
        case ApiEmployeeColumnValueType.datetime:
          return renderDateAndTime(value instanceof Date ? value : new Date(String(value)));
        case ApiEmployeeColumnValueType.bool:
          return value ? t("common:yes") : t("common:no");
        default:
          return String(value);
      }
    },
    [renderDate, renderDateAndTime, t]
  );

  const buildCustomTableColumn = useCallback(
    (column: ApiEmployeeColumn): ColumnDef<ApiEmployee> | undefined => {
      const customTable = parseCustomTableKey(column.id);
      const systemTable = parseSystemTableCustomFieldKey(column.id);
      const slug = customTable?.slug ?? systemTable?.slug;
      if (!slug) return undefined;
      const isCurrency = customTable?.isCurrency ?? systemTable?.isCurrency;

      return {
        id: column.id,
        cell: ({ row }) => {
          const table = row.original.custom_tables?.find((item) =>
            customTable
              ? item.table_id === customTable.tableId
              : item.table_type === systemTable?.tableType
          );
          if (!table?.rows.length) return EMPTY;

          // Ячейка ищется по slug поля; значение рендерится тем же кодом, что и
          // кастомные поля профиля — формат ячейки таблицы совпадает
          const values = table.rows.map((tableRow) => {
            const cell = tableRow.cells.find((item) => item.field.slug === slug);
            if (!cell) return EMPTY;
            if (isCurrency) return cell.value?.value_text || EMPTY;
            return renderFieldDataInCell(cell.field, tableRow.cells);
          });

          return renderRows(values);
        },
        ...sizeByValueType(column),
      };
    },
    [renderFieldDataInCell]
  );

  const buildCustomFieldCurrencyColumn = useCallback(
    (column: ApiEmployeeColumn): ColumnDef<ApiEmployee> | undefined => {
      const customField = parseCustomFieldKey(column.id);
      if (!customField?.isCurrency) return undefined;

      return {
        id: column.id,
        cell: ({ row }) =>
          row.original.custom_fields.find((item) => item.field.slug === customField.slug)?.value
            ?.value_text || EMPTY,
        ...sizeByValueType(column),
      };
    },
    []
  );

  const buildAdditionalCompensationColumn = useCallback(
    (column: ApiEmployeeColumn): ColumnDef<ApiEmployee> => ({
      id: column.id,
      cell: ({ row }) => {
        const items = row.original.additional_compensations;
        if (!items?.length) return EMPTY;

        const values = items.map((item) => {
          const value = getEmployeeAdditionalCompensationValue(column.id, item);
          // Сумма показывается в своей валюте: у строк доп. компенсаций она может
          // отличаться от валюты основной компенсации
          if (column.value_type === ApiEmployeeColumnValueType.money) {
            return value == null ? EMPTY : renderCurrency(String(value), item.currency);
          }
          return renderScalar(value, column);
        });

        return renderRows(values);
      },
      ...sizeByValueType(column),
    }),
    [renderCurrency, renderScalar]
  );

  // Роли идут строками в том же порядке, что команды в соседней колонке
  const buildTeamColumn = useCallback(
    (column: ApiEmployeeColumn): ColumnDef<ApiEmployee> => ({
      id: column.id,
      cell: ({ row }) => {
        const teams = row.original.teams;
        if (!teams?.length) return EMPTY;
        return renderRows(
          teams.map((team) =>
            column.id === TEAM_COLUMN_KEY
              ? team.title
              : (column.options?.find((option) => option.id === team.role)?.title ?? team.role)
          )
        );
      },
      ...sizeByValueType(column),
    }),
    []
  );

  /**
   * Определение колонки каталога, у которой нет системного рендера. Возвращает
   * `undefined` для колонок, которые должны рендериться существующими
   * определениями (системные поля и кастомные поля профиля).
   */
  return useCallback(
    (column: ApiEmployeeColumn): ColumnDef<ApiEmployee> | undefined => {
      const tableColumn = buildCustomTableColumn(column);
      if (tableColumn) return tableColumn;
      const currencyColumn = buildCustomFieldCurrencyColumn(column);
      if (currencyColumn) return currencyColumn;
      if (getEmployeeColumnEntity(column.id) === EmployeeColumnEntity.additional_compensation) {
        return buildAdditionalCompensationColumn(column);
      }
      if (column.id === TEAM_COLUMN_KEY || column.id === TEAM_ROLE_COLUMN_KEY) {
        return buildTeamColumn(column);
      }
      return undefined;
    },
    [
      buildCustomTableColumn,
      buildCustomFieldCurrencyColumn,
      buildAdditionalCompensationColumn,
      buildTeamColumn,
    ]
  );
};
